import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { useEditJD, useEditSkill, type JDEditRequest } from "@/api/jd-queries";
import { useEditJDLink } from "@/api/jd-question-queries";
import { Button } from "@/components/ui/button";
import { ErrorNote } from "@/components/ui/panel";
import {
  applySkill,
  DETAIL_LABEL,
  DraftContext,
  EMPTY,
  useDraft,
  type Details,
  type Draft,
  type DraftApi,
  type LinkPatch,
  type SkillPatch,
} from "@/lib/jd-draft-state";

/**
 * Unsaved edits to a job description — every step of its page, Details included. A change is held here,
 * shown as if it had happened, and written only when the Resource Manager presses Save, so a JD can be
 * worked on over several sittings without being published, and without one re-match per click (every
 * skill edit re-matches the JD to the bank, §20.5 step 5). Details used to save on its own button,
 * which left two ways of saving on one page; it joined this on 2026-10-15. Publishing stays separate.
 */

const sortedEntries = (c: Record<string, number> | null) =>
  Object.entries(c ?? {})
    .filter(([, n]) => n > 0)
    .sort(([a], [b]) => a.localeCompare(b));

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** The edit request's shape for the detail fields: a day's end for the expiry, so it still counts on it. */
function detailsBody(details: Partial<Details>): JDEditRequest {
  const body: JDEditRequest = { ...details };
  if ("expires_at" in details)
    body.expires_at = details.expires_at ? `${details.expires_at}T23:59:59Z` : null;
  return body;
}

export function DraftProvider({ jdId, children }: { jdId: number; children: ReactNode }) {
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const editSkill = useEditSkill(jdId);
  const editLink = useEditJDLink(jdId);
  const editJD = useEditJD(jdId);

  const skillCount = Object.keys(draft.skills).length;
  const linkCount = Object.keys(draft.links).length;
  const detailWords = [...new Set(Object.keys(draft.details).map((k) => DETAIL_LABEL[k as keyof Details]))];
  const count =
    skillCount +
    linkCount +
    detailWords.length +
    (draft.policy === null ? 0 : 1) +
    (draft.counts === null ? 0 : 1);
  const summary = [
    ...detailWords,
    skillCount && `${skillCount} skill${skillCount === 1 ? "" : "s"}`,
    linkCount && `${linkCount} question${linkCount === 1 ? "" : "s"}`,
    draft.policy && "pick policy",
    draft.counts && "questions per interview",
  ]
    .filter(Boolean)
    .join(", ");

  // The browser's own prompt: the one place a closed tab or a reload can be stopped.
  useEffect(() => {
    if (count === 0) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [count]);

  const patchSkill = useCallback((id: number, patch: SkillPatch) => {
    setDraft((d) => {
      const merged: SkillPatch = { ...d.skills[id], ...patch };
      // Another stack has other concepts, so a stack change drops any concepts chosen for the old one.
      if ("stack_path" in patch && !("concepts" in patch)) delete merged.concepts;
      return { ...d, skills: { ...d.skills, [id]: merged } };
    });
  }, []);

  const resetSkill = useCallback((id: number) => {
    setDraft((d) => {
      const skills = { ...d.skills };
      delete skills[id];
      return { ...d, skills };
    });
  }, []);

  const patchLink = useCallback((id: number, patch: LinkPatch) => {
    setDraft((d) => ({ ...d, links: { ...d.links, [id]: { ...d.links[id], ...patch } } }));
  }, []);

  const patchDetails = useCallback((patch: Partial<Details>, saved: Details) => {
    setDraft((d) => {
      const next: Partial<Details> = { ...d.details, ...patch };
      // Typing a field back to what is saved is no change, so the save bar goes away again.
      for (const key of Object.keys(patch) as (keyof Details)[]) {
        if (same(next[key], saved[key])) delete next[key];
      }
      return { ...d, details: next };
    });
  }, []);

  const setPolicy = useCallback((id: number | null, saved: number | null) => {
    setDraft((d) => ({ ...d, policy: id === saved ? null : { id } }));
  }, []);

  const setQuestionCounts = useCallback(
    (value: Record<string, number> | null, saved: Record<string, number> | null) => {
      const unchanged = same(sortedEntries(value), sortedEntries(saved));
      setDraft((d) => ({ ...d, counts: unchanged ? null : { value } }));
    },
    [],
  );

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      // Links first: a skill edit re-matches the JD, which keeps pinned and excluded matches, so the
      // choices made on them must be on record before it runs.
      for (const [qid, patch] of Object.entries(draft.links)) {
        await editLink.mutateAsync({ questionId: Number(qid), ...patch });
      }
      const body: JDEditRequest = detailsBody(draft.details);
      if (draft.policy) body.policy_id = draft.policy.id;
      if (draft.counts) body.question_counts = draft.counts.value;
      if (Object.keys(body).length > 0) await editJD.mutateAsync(body);
      for (const [sid, patch] of Object.entries(draft.skills)) {
        await editSkill.mutateAsync({ skillId: Number(sid), ...patch });
      }
      setDraft(EMPTY);
    } catch (e) {
      // What was written is on the server and will show as saved on reload; the draft stays whole, so
      // pressing Save again repeats the writes, which are all idempotent.
      setError(e);
    } finally {
      setSaving(false);
    }
  }, [draft, editJD, editLink, editSkill]);

  const api = useMemo<DraftApi>(
    () => ({
      count,
      summary,
      saving,
      error,
      skill: (s) => applySkill(s, draft.skills[s.id]),
      skillPatched: (id) => id in draft.skills,
      patchSkill,
      resetSkill,
      link: (q) => (draft.links[q.question_id] ? { ...q, ...draft.links[q.question_id] } : q),
      patchLink,
      details: (saved) => ({ ...saved, ...draft.details }),
      patchDetails,
      policyId: (saved) => (draft.policy ? draft.policy.id : saved),
      setPolicy,
      questionCounts: (saved) => (draft.counts ? draft.counts.value : saved),
      setQuestionCounts,
      save,
      discard: () => {
        setDraft(EMPTY);
        setError(null);
      },
    }),
    [
      count,
      summary,
      saving,
      error,
      draft,
      patchSkill,
      resetSkill,
      patchLink,
      patchDetails,
      setPolicy,
      setQuestionCounts,
      save,
    ],
  );

  return <DraftContext.Provider value={api}>{children}</DraftContext.Provider>;
}

/**
 * Sticks to the bottom of the page while anything is unsaved, so the way to keep it is always in view
 * and the content above it does not move when it appears.
 */
export function SaveBar({ published }: { published: boolean }) {
  const d = useDraft();
  if (d.count === 0 && !d.error) return null;
  return (
    <div
      role="region"
      aria-label="Unsaved changes"
      className="border-bank-attention bg-bank-attention-wash text-bank-attention-ink sticky bottom-0 z-20 -mx-5 mt-auto flex flex-wrap items-center gap-x-3.5 gap-y-1 border-t px-5 py-2.5 text-[13px]"
    >
      <span className="font-semibold">
        {d.count} unsaved change{d.count === 1 ? "" : "s"}
      </span>
      <span className="text-xs">
        {d.summary && <span className="font-medium">{d.summary}. </span>}
        {published
          ? "This JD is live, so saved changes reach members from their next interview."
          : "Saving does not publish — members see nothing until you publish."}
      </span>
      <div className="ml-auto flex gap-2">
        <Button size="sm" disabled={d.saving} onClick={d.discard}>
          Discard
        </Button>
        <Button
          size="sm"
          variant="primary"
          disabled={d.saving || d.count === 0}
          onClick={() => void d.save()}
        >
          {d.saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
      <div className="w-full empty:hidden">
        <ErrorNote error={d.error} />
      </div>
    </div>
  );
}
